/** Phase 5 business rules, shared between the services and the forms
 * (docs/VALIDATION.md).
 *
 * Nothing here reads a database or a clock: every fact a rule needs is passed
 * in, so the same function decides whether a form may submit and whether a
 * service may commit. The services merge these findings with the state they
 * actually read, which is what keeps a form's "looks fine" from becoming a
 * service's silent write.
 */
import { bookingVendorRecord, messageTemplateRecord, TEMPLATE_PLACEHOLDERS } from "../schemas/automation";
import { evaluation, zodIssues, type ValidationAction, type ValidationIssue } from "./issues";

// ---------------------------------------------------------------- vendors

export interface VendorFacts {
  bookingExists?: boolean;
  bookingOpen?: boolean;
  duplicateName?: boolean;
  action?: "booking_vendor.save" | "audit";
  checksComplete?: boolean;
  checkedAt?: string;
}

export function validateBookingVendor(record: Record<string, unknown>, facts: VendorFacts = {}) {
  const entity = { type: "booking_vendor" as const, ...(typeof record.id === "string" ? { id: record.id } : {}) };
  const action = facts.action ?? "audit";
  const parsed = bookingVendorRecord.safeParse(record);
  const issues: ValidationIssue[] = parsed.success ? [] : zodIssues(parsed.error.issues, entity, action);
  const add = (code: string, fields: string[], message: string) =>
    issues.push({ code: `booking_vendor.${code}`, kind: "error", entity, fields, message, blocks: ["booking_vendor.save"] });

  if (facts.bookingExists === false) add("booking_missing", [], "This booking is unavailable.");
  if (facts.bookingOpen === false) add("booking_closed", [], "A cancelled booking has no vendors to manage.");
  if (facts.duplicateName) add("duplicate", ["name"], "This vendor is already on the booking.");
  // A vendor with no way to be reached cannot be chased for a certificate.
  if (record.coiStatus === "pending" && !record.email && !record.phone) {
    issues.push({ code: "booking_vendor.no_contact", kind: "warning", entity, fields: ["email", "phone"],
      message: "Without an email or phone number nobody can chase this vendor's certificate.", blocks: [] });
  }
  return evaluation(issues, facts.checksComplete === true, facts.checkedAt);
}

// ---------------------------------------------------------------- templates

const TOKEN = /\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}/gi;

/** Every placeholder a body uses, in the order it first appears. */
export function placeholdersUsed(...bodies: (string | null | undefined)[]): string[] {
  const found: string[] = [];
  for (const body of bodies) {
    for (const match of (body ?? "").matchAll(TOKEN)) {
      const name = match[1].toLowerCase();
      if (!found.includes(name)) found.push(name);
    }
  }
  return found;
}

export const unknownPlaceholders = (...bodies: (string | null | undefined)[]) =>
  placeholdersUsed(...bodies).filter(name => !(TEMPLATE_PLACEHOLDERS as readonly string[]).includes(name));

/** A key the tick or a sequence looks up. Renaming, emptying or deleting one
 * silently stops a reminder, so it is protected — by shape rather than by a list,
 * so a reminder added later is protected the day it is written. One definition,
 * used by the service and by the form. */
export const isReservedTemplateKey = (key: string) =>
  key.startsWith("reminder.") || key.startsWith("drip.") || ["lead_ack", "contract_pdf"].includes(key);

export interface TemplateFacts {
  duplicateKey?: boolean;
  /** Defaults to the shape rule above; pass it only to override. */
  reserved?: boolean;
  action?: "message_template.save" | "audit";
  checksComplete?: boolean;
  checkedAt?: string;
}

export function validateMessageTemplate(record: Record<string, unknown>, facts: TemplateFacts = {}) {
  const entity = { type: "message_template" as const, ...(typeof record.id === "string" ? { id: record.id } : {}) };
  const action = facts.action ?? "audit";
  const parsed = messageTemplateRecord.safeParse(record);
  const issues: ValidationIssue[] = parsed.success ? [] : zodIssues(parsed.error.issues, entity, action);
  const add = (code: string, fields: string[], message: string, kind: "error" | "warning" = "error") =>
    issues.push({ code: `message_template.${code}`, kind, entity, fields, message, blocks: kind === "error" ? ["message_template.save"] : [] });

  const sms = typeof record.smsBody === "string" ? record.smsBody : null;
  const emailBody = typeof record.emailBody === "string" ? record.emailBody : null;
  const emailSubject = typeof record.emailSubject === "string" ? record.emailSubject : null;
  if (!sms?.trim() && !emailBody?.trim()) add("empty", ["emailBody"], "Give the template an email body, a text body, or both.");
  if (emailBody?.trim() && !emailSubject?.trim()) add("subject_missing", ["emailSubject"], "An email needs a subject line.");
  const unknown = unknownPlaceholders(sms, emailSubject, emailBody);
  if (unknown.length) {
    add("unknown_placeholder", ["emailBody"], `This template uses ${unknown.map(name => `{{${name}}}`).join(", ")}, which nothing can fill in. A client would receive the braces.`);
  }
  if (facts.duplicateKey) add("duplicate_key", ["key"], "Another template already uses this key.");
  const reserved = facts.reserved ?? (typeof record.key === "string" && isReservedTemplateKey(record.key));
  if (reserved && record.active === 0) {
    add("reserved_inactive", ["active"], "Turning this template off stops the reminder that uses it. It stays available but nothing will be sent.", "warning");
  }
  return evaluation(issues, facts.checksComplete === true, facts.checkedAt);
}

// ---------------------------------------------------------------- queue

export interface QueueFacts {
  /** The message's current status, as actually read. */
  status?: string;
  /** `false` when the member cannot see the message's venue. */
  visible?: boolean;
  action?: "message.release" | "message.cancel" | "message.save" | "audit";
  checksComplete?: boolean;
  checkedAt?: string;
}

const RELEASABLE = ["held"];
const CANCELLABLE = ["held", "queued"];

export function validateQueuedMessage(record: Record<string, unknown>, facts: QueueFacts = {}) {
  const entity = { type: "message" as const, ...(typeof record.id === "string" ? { id: record.id } : {}) };
  const action = facts.action ?? "audit";
  const issues: ValidationIssue[] = [];
  const add = (code: string, message: string, blocks: ValidationAction[]) =>
    issues.push({ code: `message.${code}`, kind: "error", entity, fields: ["status"], message, blocks });

  const status = facts.status ?? (typeof record.status === "string" ? record.status : undefined);
  if (facts.visible === false) add("not_visible", "This message is unavailable.", ["message.release", "message.cancel", "message.save"]);
  if (status && !RELEASABLE.includes(status) && (action === "message.release" || action === "audit")) {
    add("not_releasable", status === "cancelled" ? "This message was cancelled." : `A message that is ${status.replaceAll("_", " ")} has already left the queue.`, ["message.release"]);
  }
  if (status && !CANCELLABLE.includes(status) && (action === "message.cancel" || action === "audit")) {
    add("not_cancellable", `A message that is ${status.replaceAll("_", " ")} can no longer be cancelled.`, ["message.cancel"]);
  }
  if (status && status !== "held" && (action === "message.save" || action === "audit")) {
    add("not_editable", "Only a held message can be edited.", ["message.save"]);
  }
  return evaluation(issues, facts.checksComplete === true, facts.checkedAt);
}
